<template>
  <component
    :is="componentType"
    :type="inputType"
    v-bind="$attrs"
    v-on="$listeners"
    :value="value"
    :items="usesItems ? items : undefined"
    :item-text="usesItems ? itemText : undefined"
    :item-value="usesItems ? itemValue : undefined"
    :multiple="multiple"
    :chips="chips"
    :small-chips="smallChips"
    :deletable-chips="deletableChips"
    :rules="computedRules"
    :color="color"
    :item-color="itemColor"
    :outlined="outlined"
    :dense="dense"
    :disabled="disabled"
    :readonly="readonly"
    :clearable="clearable"
    :persistent-hint="persistentHint"
    :persistent-placeholder="persistentPlaceholder"
    :hint="effectiveHint"
    :placeholder="placeholder"
    :prefix="prefix"
    :suffix="suffix"
    :prepend-icon="prependIcon"
    :prepend-inner-icon="prependInnerIcon"
    :append-icon="appendIcon"
    :background-color="backgroundColor"
    :hide-details="hideDetails"
    :rows="rows"
    :counter="counter"
    :maxlength="maxlength"
    @input="handleInput"
    @change="handleChange"
    @blur="handleBlur"
    @focus="handleFocus"
    @click:clear="handleClear"
  >
    <!-- Label with required indicator -->
    <template v-slot:label v-if="label || showRequired">
      <span>
        {{ label }}
        <span v-if="showRequired" class="font-weight-bold error--text">*</span>
      </span>
    </template>

    <!-- Success icon when valid -->
    <template v-slot:append v-if="showSuccessIcon && isValid && touched">
      <v-icon small color="success" class="mr-1 mt-1">
        mdi-check-circle
      </v-icon>
    </template>

    <!-- Pass through selection slot for v-select/v-autocomplete -->
    <template v-if="usesItems" v-slot:selection="{ item, index }">
      <slot name="selection" :item="item" :index="index">
        <v-chip
          v-if="chips && multiple"
          :small="smallChips"
          :close="deletableChips"
          @click:close="removeChip(item)"
        >
          {{ getItemText(item) }}
        </v-chip>
        <span v-else-if="multiple && index === 0" :class="selectionClass">
          {{ getItemText(item) }}
          <span v-if="value.length > 1" class="grey--text text-caption ml-1">
            (+{{ value.length - 1 }})
          </span>
        </span>
        <span v-else-if="!multiple" :class="selectionClass">
          {{ getItemText(item) }}
        </span>
      </slot>
    </template>

    <!-- Pass through prepend-item slot for v-select/v-autocomplete -->
    <template
      v-if="
        usesItems && ($slots['prepend-item'] || $scopedSlots['prepend-item'])
      "
      v-slot:prepend-item
    >
      <slot name="prepend-item"></slot>
    </template>

    <!-- Pass through append-item slot for v-select/v-autocomplete -->
    <template
      v-if="usesItems && ($slots['append-item'] || $scopedSlots['append-item'])"
      v-slot:append-item
    >
      <slot name="append-item"></slot>
    </template>

    <!-- Pass through item slot for v-select/v-autocomplete -->
    <template v-if="usesItems" v-slot:item="data">
      <slot name="item" v-bind="data">
        <template v-if="multiple">
          <v-list-item v-bind="data.attrs" v-on="data.on">
            <v-list-item-action>
              <v-checkbox
                :input-value="data.attrs.inputValue"
                :color="themeColor"
                hide-details
                readonly
              ></v-checkbox>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span :class="itemClass">{{ getItemText(data.item) }}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item v-bind="data.attrs" v-on="data.on">
            <v-list-item-content>
              <v-list-item-title>
                <span :class="itemClass">{{ getItemText(data.item) }}</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </slot>
    </template>

    <!-- Pass through other common slots -->
    <template v-slot:prepend>
      <slot name="prepend"></slot>
    </template>

    <template v-slot:prepend-inner>
      <slot name="prepend-inner"></slot>
    </template>

    <!-- Undo button + passthrough append-outer slot (UPDATABLE MODE) -->
    <template v-slot:append-outer v-if="updatable && isUpdated">
      <div class="d-flex align-center" style="gap: 4px">
        <!-- Custom append-outer content from parent -->
        <slot name="append-outer"></slot>
        <!-- Built-in undo button -->
        <DialogUndoButton @click="handleUndo" />
      </div>
    </template>

    <!-- Regular append-outer slot (NON-UPDATABLE MODE) -->
    <template
      v-slot:append-outer
      v-else-if="$slots['append-outer'] || $scopedSlots['append-outer']"
    >
      <slot name="append-outer"></slot>
    </template>

    <template v-slot:message="{ message }">
      <slot
        v-if="hasCustomHintSlot && message === hintSlotPlaceholder"
        name="hint"
      />
      <slot v-else name="message" :message="message">
        {{ message }}
      </slot>
    </template>
  </component>
</template>

<script>
import {
  VTextField,
  VSelect,
  VAutocomplete,
  VTextarea,
  VCheckbox,
  VListItem,
  VListItemAction,
  VListItemContent,
  VListItemTitle,
} from "vuetify/lib";
import DialogUndoButton from "@/components/misc/ui/buttons/DialogUndoButton.vue";

export default {
  name: "MyTraceInput",
  inheritAttrs: false,

  components: {
    VTextField,
    VSelect,
    VAutocomplete,
    VTextarea,
    VCheckbox,
    VListItem,
    VListItemAction,
    VListItemContent,
    VListItemTitle,
    DialogUndoButton,
  },

  props: {
    /* Core */
    value: null,
    label: {
      type: String,
      default: "",
    },
    rules: {
      type: Array,
      default: () => [],
    },

    /* Type control */
    type: {
      type: String,
      default: "text", // text | select | autocomplete | textarea
      validator: (value) =>
        [
          "text",
          "number",
          "tel",
          "email",
          "url",
          "password",
          "select",
          "autocomplete",
          "textarea",
        ].includes(value),
    },

    /* Select / Autocomplete */
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: [String, Function],
      default: "text",
    },
    itemValue: {
      type: String,
      default: "value",
    },
    itemClass: {
      type: String,
      default: "",
    },
    selectionClass: {
      type: String,
      default: "",
    },
    multiple: Boolean,
    chips: Boolean,
    smallChips: Boolean,
    deletableChips: Boolean,

    /* Textarea */
    rows: {
      type: [Number, String],
      default: 3,
    },

    /* Validation & Display */
    required: Boolean,
    // Like required, but treats false as a valid value (use for Yes/No boolean selects)
    requiredEither: Boolean,
    showSuccessIcon: {
      type: Boolean,
      default: true,
    },

    /* Updatable state */
    updatable: Boolean,
    isUpdated: Boolean,
    originalValue: null,

    /* UI */
    dense: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    readonly: Boolean,
    clearable: Boolean,
    persistentHint: Boolean,
    persistentPlaceholder: Boolean,
    hint: String,
    placeholder: String,
    prefix: String,
    suffix: String,
    prependIcon: String,
    prependInnerIcon: String,
    appendIcon: String,
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    counter: [Boolean, Number, String],
    maxlength: [Number, String],

    /* Theming */
    color: {
      type: String,
      default: null,
    },
    itemColor: {
      type: String,
      default: null,
    },
    requiredColor: {
      type: String,
      default: "updated",
    },
    validColor: {
      type: String,
      default: "",
    },
    updatedColor: {
      type: String,
      default: "updated",
    },
  },

  data() {
    const hasValue =
      this.value !== null &&
      this.value !== "" &&
      !(Array.isArray(this.value) && this.value.length === 0);
    return {
      internalValue: this.value,
      touched: hasValue,
      // VInput only shows the messages area for hints when `hint` is truthy; use this
      // placeholder when the parent uses the #hint slot so rich content can render.
      hintSlotPlaceholder: "\u00a0",
    };
  },

  computed: {
    componentType() {
      switch (this.type) {
        case "select":
          return VSelect;
        case "autocomplete":
          return VAutocomplete;
        case "textarea":
          return VTextarea;
        default:
          return VTextField;
      }
    },

    inputType() {
      // Return the HTML input type for VTextField
      if (["select", "autocomplete", "textarea"].includes(this.type)) {
        return undefined;
      }
      return this.type;
    },

    normalizedValue() {
      if (this.multiple) return Array.isArray(this.value) ? this.value : [];
      if (this.usesItems) return this.value ?? null;

      // For number inputs, return the number value as-is
      if (this.type === "number") {
        return this.value;
      }

      return typeof this.value === "string" ? this.value : "";
    },

    usesItems() {
      return this.type === "select" || this.type === "autocomplete";
    },

    computedRules() {
      const allRules = [...this.rules]; // Start with provided rules

      const hasRequiredRule = this.rules.some((rule) => {
        const result = rule("");
        return (
          result !== true &&
          typeof result === "string" &&
          result.includes("required")
        );
      });

      // Add required rule if needed and not already present
      if (this.required && !hasRequiredRule) {
        allRules.unshift((v) => {
          if (Array.isArray(v)) return v.length > 0 || "This field is required";
          return !!v || "This field is required";
        });
      }

      // requiredEither: like required but treats false as valid (Yes/No boolean selects)
      if (this.requiredEither && !hasRequiredRule) {
        allRules.unshift((v) => {
          if (Array.isArray(v)) return v.length > 0 || "This field is required";
          return (v !== null && v !== undefined && v !== "") || "This field is required";
        });
      }

      return allRules;
    },
    showRequired() {
      return this.required || this.requiredEither || this.isRequired;
    },

    isRequired() {
      if (this.required || this.requiredEither) return true;
      return this.rules.some((rule) => {
        const result = rule("");
        return result !== true;
      });
    },

    isValid() {
      const val = this.normalizedValue;

      if (this.requiredEither) {
        // false is a valid value for Yes/No selects
        if (val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0))
          return false;
      } else {
        if (val === null || val === "" || (Array.isArray(val) && val.length === 0))
          return false;
      }

      if (!this.computedRules.length) return false;
      return this.computedRules.every((rule) => rule(val) === true);
    },

    backgroundColor() {
      // Priority:
      // 1. Not valid & required -> requiredColor
      // 2. Updated -> updatedColor
      // 3. Valid -> validColor
      // 4. Default -> empty

      // if (this.showRequired && !this.isValid) {
      //   return this.requiredColor;
      // }
      if (this.showRequired && !this.isValid && this.touched) {
        return this.$vuetify.theme.dark ? "red lighten-3" : "red lighten-4";
      }

      if (this.updatable && this.isUpdated) {
        return this.updatedColor;
      }

      if (this.isValid && this.validColor) {
        return this.validColor;
      }

      return this.$attrs["background-color"] ?? "";
    },

    themeColor() {
      return this.color || this.$vuetify.theme.currentTheme.primary;
    },

    themeItemColor() {
      return this.itemColor || this.themeColor;
    },

    hasCustomHintSlot() {
      return !!(this.$scopedSlots.hint || this.$slots.hint);
    },

    effectiveHint() {
      if (this.hasCustomHintSlot) {
        return this.hintSlotPlaceholder;
      }
      return this.hint;
    },
  },

  methods: {
    handleInput(value) {
      let normalized = value;

      // 🔧 normalize autocomplete clears
      if (this.usesItems) {
        if (
          value === undefined ||
          value === "" ||
          (this.multiple && Array.isArray(value) && value.length === 0)
        ) {
          normalized = this.multiple ? [] : null;
        }
      }

      this.internalValue = normalized;
      this.touched = true;
      this.$emit("input", normalized);
    },

    handleChange(value) {
      this.$emit("change", value);
    },

    handleBlur(event) {
      this.touched = true;
      this.$emit("blur", event);
    },

    handleFocus(event) {
      this.$emit("focus", event);
    },

    handleClear() {
      let clearedValue;

      if (this.multiple) {
        clearedValue = [];
      } else if (this.isAutocomplete || this.isSelect) {
        clearedValue = null;
      } else {
        clearedValue = "";
      }

      this.internalValue = clearedValue;
      this.touched = false;
      this.$emit("input", clearedValue);
      this.$emit("change", clearedValue);

      this.$nextTick(() => {
        this.$refs.input?.resetValidation?.();
      });
    },

    handleUndo() {
      const original =
        this.originalValue !== undefined ? this.originalValue : "";
      this.$emit("input", original);
      this.$emit("undo", original);
    },

    removeChip(item) {
      const itemValue = typeof item === "object" ? item[this.itemValue] : item;
      const newValue = this.value.filter((v) => v !== itemValue);
      this.$emit("input", newValue);
      this.$nextTick(() => {
        this.$emit("change", newValue);
      });
    },

    getItemText(item) {
      if (typeof this.itemText === "function") {
        return this.itemText(item);
      }
      return typeof item === "object" ? item[this.itemText] : item;
    },

    focus() {
      this.$refs.input?.focus();
    },

    blur() {
      this.$refs.input?.blur();
    },
  },

  watch: {
    value(newVal) {
      this.internalValue = newVal;
      if (
        newVal !== null &&
        newVal !== "" &&
        !(Array.isArray(newVal) && newVal.length === 0)
      ) {
        this.touched = true;
      }
    },
  },
};
</script>

<style scoped>
/* Small chip styling for multiple selects */
::v-deep .v-chip.v-chip--small {
  height: 20px !important;
  font-size: 12px !important;
  margin: 2px 3px !important;
  border-radius: 12px !important;
  padding: 0 6px !important;
}

/* Compact input styling */
::v-deep .v-text-field__details {
  margin-top: 2px;
}

/* Custom background colors */
::v-deep .v-input.required .v-input__control {
  background-color: rgba(var(--v-error-base), 0.05);
}

::v-deep .v-input.updated .v-input__control {
  background-color: rgba(var(--v-warning-base), 0.1);
}
</style>
